/**
 * Resolves the choices for a "dynamic field" — a form field whose options come
 * from answers recorded earlier in the same transaction rather than from a
 * hardcoded list.
 *
 * A field opts in via `settings.isDynamicField`, and names its sources via
 * `settings.dynamicFields = [{ step: <stepUid>, field: <fieldUid> }]`.
 *
 * Everything is read from the transaction history that the page has already
 * loaded, so there is no network call. Values are carried through untouched —
 * a RESOURCE answer stays the `{id, fields, category}` object the submit
 * serializer already expects, and a DATE answer stays the `{date, timezone,
 * ...}` object the picker already expects. Only the display label is derived.
 */

export type DynamicItem = {
  /** Stable de-dupe / React key. Semantic, not textual. */
  key: string;
  /** What the user sees. */
  label: string;
  /** What gets written into Formik state — the stored value, unmodified. */
  value: any;
  /** inputComponent of the SOURCE field the value came from. */
  inputComponent: string;
};

type ResolveOptions = {
  /** Previously-saved answer, so an existing selection never drops off the list. */
  savedValue?: any;
  /**
   * Label for a RESOURCE value. Resource types are user-defined and their
   * `fields` bag is arbitrary, so the caller supplies the heuristic it already
   * uses elsewhere. Falls back to the resource id when omitted.
   */
  getResourceLabel?: (resource: any) => string;
};

/**
 * Newest-first comparison key. `updateTime` leads because that is what the
 * backend orders events by; the rest are fallbacks for events that predate it.
 */
const timeOf = (event: any): number =>
  new Date(event?.updateTime || event?.editedAt || event?.updatedAt || event?.createdAt || 0).getTime();

/** DATE answers are stored as `{ date, timezone, includeTime, includeTimezone, ... }`. */
const formatDateLabel = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();

  const raw = value.date;
  if (!raw) return '';

  const [datePart, timePart] = String(raw).split('T');
  const base = value.includeTime && timePart ? `${datePart} ${timePart.slice(0, 5)}` : datePart;

  return value.includeTimezone && value.timezone ? `${base} (${value.timezone})` : base;
};

/** Source types whose text holds a list of values rather than a single one. */
const LIST_LIKE_TEXT = new Set(['TEXT', 'TEXTAREA', 'TEXTARRAY', 'NUMBER']);

/**
 * A TEXT answer can carry several values in one string — the "Counter" setting
 * asks users to comma-separate them, and the count badge on the transaction
 * view splits on this same pattern (see getCommaSeparatedCount in details.tsx).
 * Split so each value becomes its own choice, and so the count and the option
 * list can never disagree.
 *
 * Discrete types (SELECT / RADIO / CHECKBOX) are never split: their values are
 * already single choices and may legitimately contain commas or spaces.
 */
const splitScalar = (value: string, inputComponent: string): string[] =>
  LIST_LIKE_TEXT.has(inputComponent) ? value.split(/[\s,]+/) : [value];

/**
 * Best-effort source type for a value we were handed without one (the saved
 * answer). Scalars report as SELECT so an already-chosen value is never split.
 */
const inferComponent = (value: any): string => {
  if (value && typeof value === 'object') {
    if (value.id || value._id) return 'RESOURCE';
    if (value.date) return 'DATE';
  }
  return 'SELECT';
};

export const resolveDynamicItems = (settings: any, history: any[], options: ResolveOptions = {}): DynamicItem[] => {
  const { savedValue, getResourceLabel } = options;

  const items: DynamicItem[] = [];
  const seen = new Set<string>();

  const collect = (raw: any, inputComponent: string) => {
    // Answers are stored array-wrapped for some types and bare for others.
    const list = Array.isArray(raw) ? raw : raw !== undefined && raw !== null ? [raw] : [];

    const add = (key: string, label: string, value: any) => {
      if (seen.has(key)) return;
      seen.add(key);
      items.push({ key, label, value, inputComponent });
    };

    for (const value of list) {
      if (value === undefined || value === null || value === '') continue;

      if (inputComponent === 'RESOURCE' && typeof value === 'object') {
        const id = value.id ?? value._id;
        if (!id) continue;
        add(`res:${id}`, getResourceLabel?.(value) || String(id), value);
      } else if (inputComponent === 'DATE' && typeof value === 'object') {
        const label = formatDateLabel(value);
        if (!label) continue;
        add(`date:${value.date}:${value.timezone ?? ''}`, label, value);
      } else if (typeof value === 'string' || typeof value === 'number') {
        // One stored answer can yield several choices — see splitScalar.
        for (const part of splitScalar(String(value), inputComponent)) {
          const label = part.trim();
          if (!label) continue;
          add(`s:${label.toLowerCase()}`, label, label);
        }
      }
      // FILE / CAMERA / ESIGN keep their payload in `value.files`, so there is
      // nothing here to offer as a choice.
    }
  };

  const mappings = Array.isArray(settings?.dynamicFields) ? settings.dynamicFields : [];

  mappings.forEach((mapping: any) => {
    if (!mapping?.step || !mapping?.field) return;

    // Only the most recent submission of the source step counts. A reset marks
    // events rather than deleting them, so the newest event is the live answer.
    const latest = (history || [])
      .filter((item: any) => item?.step?.uid === mapping.step && Array.isArray(item?.data))
      .reduce((best: any, item: any) => (!best || timeOf(item) >= timeOf(best) ? item : best), null);

    if (!latest) return;

    const entry = latest.data.find((d: any) => d?.uid === mapping.field || d?.field === mapping.field);
    if (!entry) return;

    collect(entry.value?.value, String(entry.inputComponent ?? ''));
  });

  if (savedValue !== undefined && savedValue !== null) {
    collect(savedValue, inferComponent(Array.isArray(savedValue) ? savedValue[0] : savedValue));
  }

  return items;
};

/** Label-only view, for callers that just need `string[]` of choices. */
export const resolveDynamicOptions = (settings: any, history: any[], savedValue?: any): string[] =>
  resolveDynamicItems(settings, history, { savedValue }).map((item) => item.label);
